import { ApolloError, gql, useApolloClient, useQuery } from "@apollo/client";
import { useCallback, useEffect, useState } from "react";
import { JobsCount, JobsCountVariables } from "./schema/JobsCount";
import {
  QueryUseCaseJobs,
  QueryUseCaseJobsVariables
} from "./schema/QueryUseCaseJobs";

export const jobsQuery = gql`
  query QueryUseCaseJobs(
    $solutionKey: Key!
    $useCaseKey: Key!
    $useCaseVersion: String
    $scheduler: String
    $skipUntilId: ID
    $take: Int
  ) {
    solutions {
      solution(solutionKey: $solutionKey) {
        key
        useCases {
          useCase(useCaseKey: $useCaseKey, version: $useCaseVersion) {
            calculations(
              scheduler: $scheduler
              skipUntilId: $skipUntilId
              take: $take
            ) {
              id
              detail {
                completed
                created
                started
                state
                username {
                  firstName
                  lastName
                  name
                }
              }
              inputData {
                detail {
                  metaData {
                    displayName
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const jobsCountQuery = gql`
  query JobsCount(
    $solutionKey: Key!
    $useCaseKey: Key!
    $useCaseVersion: String
    $scheduler: String
  ) {
    solutions {
      solution(solutionKey: $solutionKey) {
        key
        useCases {
          useCase(useCaseKey: $useCaseKey, version: $useCaseVersion) {
            calculationsCount(scheduler: $scheduler)
          }
        }
      }
    }
  }
`;

export const useQueryUseCaseJobsCount = (variables: JobsCountVariables) =>
  useQuery<JobsCount, JobsCountVariables>(jobsCountQuery, {
    variables
  });

export enum JobState {
  Completed = "Completed",
  Failed = "Failed",
  InProgress = "InProgress",
  Scheduled = "Scheduled"
}

interface User {
  firstName: string | null;
  lastName: string | null;
  name: string;
}
export interface RowData {
  id: string;
  completed: Date | null;
  created: Date | null;
  started: Date | null;
  state: JobState;
  username: User;
  displayName: string | undefined;
}

export const useQueryUseCaseCalculations = (
  solutionKey: string,
  useCaseKey: string,
  useCaseVersion?: string | null,
  scheduler?: string | null,
  skipUntilId?: string | null,
  take?: number | null
) => {
  const client = useApolloClient();

  const [loading, setLoading] = useState<boolean>(true);
  const [allJobs, setAllJobs] = useState<RowData[]>([]);
  const [errors, setErrors] = useState<ApolloError[]>([]);
  const loadUseCaseJobs = useCallback(
    async (variables: QueryUseCaseJobsVariables) => {
      let { skipUntilId, take, ...rest } = variables;
      try {
        setLoading(true);
        const { errors, data } = await client.query<
          QueryUseCaseJobs,
          QueryUseCaseJobsVariables
        >({
          query: jobsQuery,
          variables: { skipUntilId, take, ...rest },
          errorPolicy: "all"
        });
        if (errors) {
          setErrors(e => [...e, new ApolloError({ graphQLErrors: errors })]);
        }
        const useCase = data.solutions.solution?.useCases?.useCase;
        const calculations = useCase?.calculations?.map(
          ({
            id,
            detail: { completed, created, started, state, username },
            inputData
          }) => ({
            id,
            completed,
            created,
            started,
            state,
            username,
            displayName: inputData.detail?.metaData.displayName
              ? inputData.detail.metaData.displayName
              : undefined
          })
        );

        setAllJobs(calculations || []);
        setLoading(false);
      } catch (error) {
        setErrors(e => [error as ApolloError, ...e]);
        setLoading(false);
      }
    },
    [client]
  );
  useEffect(() => {
    const loadJobs = async () => {
      await loadUseCaseJobs({
        solutionKey,
        useCaseKey,
        useCaseVersion: useCaseVersion ?? null,
        scheduler: scheduler ?? null,
        skipUntilId: skipUntilId ?? null,
        take: take ?? null
      });
    };
    loadJobs();
  }, [
    loadUseCaseJobs,
    solutionKey,
    useCaseKey,
    useCaseVersion,
    scheduler,
    take,
    skipUntilId
  ]);

  const reloadJobs = useCallback(async () => {
    if (!useCaseKey) return;
    setAllJobs([]);

    await loadUseCaseJobs({
      solutionKey,
      useCaseKey,
      useCaseVersion: useCaseVersion ?? null,
      scheduler: scheduler ?? null,
      skipUntilId: null,
      take: take ?? null
    });
  }, [
    solutionKey,
    loadUseCaseJobs,
    useCaseKey,
    useCaseVersion,
    scheduler,
    take
  ]);

  // return state
  return {
    allJobs,
    loading,
    reloadJobs,
    errors
  };
};
